import React, {
  ElementType,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useState,
  CSSProperties,
} from "react";
import { DayModifiers, DayProps } from "react-day-picker";

import { Placement } from "@popperjs/core";
import classnames from "classnames";
import moment from "moment";

import { DatePickerForm } from "./DatePickerForm";
import { SelectDateTarget } from "./SelectDateTarget";
import {
  StyledConfirmDialog,
  StyledSelectDate,
  StyledSelectDateButton,
} from "./Styles";
import { DaysToModify } from "./types";
import { formatDate } from "../../utils/dateUtils";
import { useLocalization } from "../Localization";
import { Menu } from "../Menu/Menu";
import { Tooltip } from "../Tooltip/Tooltip";

const getUtcTimestampFromDate = (date: Date): number => {
  return moment
    .utc([date.getFullYear(), date.getMonth(), date.getDate()])
    .unix();
};

const isDayInRange = (day: Date, data: DaysToModify): boolean => {
  if (data.repeating) {
    const from = new Date(
      day.getFullYear(),
      data.from.getMonth(),
      data.from.getDate()
    );
    const to = new Date(
      day.getFullYear(),
      data.to.getMonth(),
      data.to.getDate()
    );
    return day >= from && day <= to;
  }

  return day >= data.from && day <= data.to;
};

type TriggerStringType = "text" | "icon" | "inline";
type TriggerFunctionType = (text: string) => JSX.Element;

export interface ISelectDate {
  /** Change mode - can be atomic and instant */
  changeMode?: "atomic" | "instant";
  /** Trigger - can be text, icon, inline, or custom render function */
  trigger?: TriggerStringType | TriggerFunctionType;
  /** Save button text */
  saveButtonText?: string;
  /** Cancel button text */
  cancelButtonText?: string;
  /** Clear button text */
  clearButtonText?: string;
  /** Modal header text */
  modalHeaderText?: string;
  /** Modal cancel button text */
  modalCancelBtnText?: string;
  /** Modal discard button text */
  modalDiscardBtnText?: string;
  /** Modal discard message text */
  modalDiscardMessage?: string;
  /** Tooltip text */
  tooltipText?: string;
  /** Called when day is clicked */
  onDayClick?: (day: Date, modifiers: DayModifiers) => void;
  /** Save changes in atomic mode */
  onSave?: (dates?: { from: number; to: number }) => void;
  /** On cancel closes datepicker or opens disard modal in atomic mode */
  onCancel?: () => void;
  /** Clear dates when they are not required */
  onClear?: () => void;
  /** Use this callback to get is picker visible */
  onToggleDatePicker?: (value: boolean) => void;
  /** Set selected day or days */
  selectedDays?: { from: number; to: number };
  /** Set month */
  defaultMonth?: Date;
  /** Set selection range */
  selectionMode?: string;
  /** Set is date required */
  required?: boolean;
  /** Set default show on date picker (only for text and icon mode) */
  defaultShowDatePicker?: boolean;
  /** Long date format (Always show year) */
  longDateFormat?: boolean;
  /** Set is date required */
  defaultTimezoneAware?: boolean;
  /** Set first day of week */
  firstDayOfWeek?: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  /** One of icon types */
  icon?: ElementType;
  /** Default Label text */
  defaultLabelText?: string;
  /** Date format */
  dateFormat?: string;
  /** Menu classes */
  menuClassName?: string;
  /** Target element classes */
  targetClassName?: string;
  /** Target element text classes */
  targetTextClassName?: string;
  /** Target element icon classes */
  targetIconClassName?: string;
  /** Background style for clickable element after the menu is open (opacity, color, etc...) */
  backgroundElementClass?: string;
  /** Days to modify */
  daysToModify?: DaysToModify[];
  /** Array of weekend days */
  weekends?: number[];
  /** Weekend label */
  weekendLabel?: string;
  /** Array of nonworking week days */
  nonWorkingDaysOfWeek?: number[];
  /** Nonworking week days label */
  nonWorkingDaysOfWeekLabel?: string;
  /** Allows weekends to be selected */
  weekendIsSelectable?: boolean;
  /** Popper Tooltip style */
  popperTooltipStyle?: CSSProperties;
  /** Popper Tooltip class name*/
  popperTooltipClassName?: string;
  /** Popper class name*/
  popperClassName?: string;
  /** Position of selectDate menu */
  position?: Placement;
  /** Disable days before specified date */
  disableDaysBefore?: Date;
}

export const SelectDate: FC<ISelectDate> = ({
  changeMode: mode = "instant",
  trigger: labelType = "text",
  saveButtonText = "Save",
  cancelButtonText = "Cancel",
  clearButtonText = "Clear",
  modalHeaderText = "Discard changes?",
  modalDiscardMessage = "All unsaved changes will be lost.",
  modalDiscardBtnText = "OK",
  modalCancelBtnText = "Cancel",
  onDayClick,
  onSave,
  onCancel,
  onToggleDatePicker,
  required: dateRequired = false,
  defaultTimezoneAware = false,
  longDateFormat = false,
  defaultShowDatePicker = false,
  firstDayOfWeek = 0,
  selectedDays,
  selectionMode = "custom",
  menuClassName,
  targetClassName,
  icon,
  defaultLabelText = "Set...",
  targetTextClassName,
  targetIconClassName,
  backgroundElementClass,
  dateFormat: passedDateFormat,
  defaultMonth,
  daysToModify = [],
  weekends = [],
  weekendLabel = "Weekend",
  nonWorkingDaysOfWeek = [],
  nonWorkingDaysOfWeekLabel = "Unavailable",
  weekendIsSelectable = false,
  tooltipText,
  popperTooltipClassName,
  popperClassName,
  popperTooltipStyle,
  position,
  disableDaysBefore,
}) => {
  const [timezoneAware, setTimezoneAware] = useState(defaultTimezoneAware);
  const localization = useLocalization();
  const dateFormat = passedDateFormat
    ? passedDateFormat
    : localization.dateFormat;

  const labelText = useMemo(() => {
    if (!selectedDays) {
      return defaultLabelText;
    } else {
      const endDate = timezoneAware
        ? moment.unix(selectedDays.to).local()
        : moment.unix(selectedDays.to).utc();

      const startDate = timezoneAware
        ? moment.unix(selectedDays.from).local()
        : moment.unix(selectedDays.from).utc();

      const formattedEndDate = formatDate(endDate, dateFormat, longDateFormat);
      const formattedStartDate = formatDate(
        startDate,
        dateFormat,
        longDateFormat
      );

      if (selectedDays.from === selectedDays.to) {
        return formattedEndDate;
      }

      return `${formattedStartDate} - ${formattedEndDate}`;
    }
  }, [
    selectedDays,
    dateFormat,
    timezoneAware,
    defaultLabelText,
    longDateFormat,
  ]);

  const modifiedDates = useMemo(() => {
    if (
      typeof selectedDays === "object" &&
      !!selectedDays.from &&
      !!selectedDays.to
    ) {
      if (timezoneAware) {
        return {
          from: moment.unix(selectedDays.from).toDate(),
          to: moment.unix(selectedDays.to).toDate(),
        };
      }
      const offsetFrom = moment.unix(selectedDays.from).utcOffset() * 60;
      const offsetTo = moment.unix(selectedDays.to).utcOffset() * 60;
      return {
        from: new Date((selectedDays.from - offsetFrom) * 1000),
        to: new Date((selectedDays.to - offsetTo) * 1000),
      };
    }
  }, [selectedDays, timezoneAware]);

  const handleModifiedSave = useCallback(
    (selectedDates) => {
      setTimezoneAware(false);
      if (typeof onSave === "function") {
        if (
          typeof selectedDates === "object" &&
          Object.prototype.hasOwnProperty.call(selectedDates, "from") &&
          Object.prototype.hasOwnProperty.call(selectedDates, "to")
        ) {
          onSave({
            from: getUtcTimestampFromDate(selectedDates.from),
            to: getUtcTimestampFromDate(selectedDates.to),
          });
        } else {
          onSave(undefined);
        }
      }
    },
    [onSave]
  );

  const getDefaultMonth = useCallback(() => {
    let date = new Date();

    if (typeof selectedDays === "object" && !!selectedDays.from) {
      date = timezoneAware
        ? new Date(selectedDays.from * 1000)
        : new Date(
            (selectedDays.from -
              moment.unix(selectedDays.from).utcOffset() * 60) *
              1000
          );
    } else if (defaultMonth) {
      date = defaultMonth;
    }

    // forcing second day in month
    return new Date(date.getFullYear(), date.getMonth(), 2);
  }, [defaultMonth, selectedDays, timezoneAware]);

  const [month, setMonth] = useState(getDefaultMonth);

  const handleMonthChange = useCallback(
    (month) => {
      setMonth(month);
    },
    [setMonth]
  );

  const [newDates, setNewDates] = useState(modifiedDates);
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(defaultShowDatePicker);

  const disabledSaveButton = useMemo(() => {
    return (
      modifiedDates?.from === newDates?.from &&
      modifiedDates?.to === newDates?.to
    );
  }, [modifiedDates, newDates]);

  useEffect(() => {
    setShowDatePicker(defaultShowDatePicker);
  }, [defaultShowDatePicker]);

  useEffect(() => {
    setNewDates(modifiedDates);
  }, [modifiedDates]);

  useEffect(() => {
    typeof onToggleDatePicker === "function" &&
      onToggleDatePicker(showDatePicker);
  }, [showDatePicker, onToggleDatePicker]);

  useEffect(() => {
    if (!showDatePicker) {
      setMonth(getDefaultMonth());
    }
  }, [showDatePicker, getDefaultMonth]);

  const handleSave = useCallback(() => {
    handleModifiedSave(newDates);
    setShowDatePicker(false);
  }, [newDates, handleModifiedSave]);

  const handleChange = useCallback(
    (dates) => {
      if (mode !== "atomic") {
        handleModifiedSave(dates);
      }
      setNewDates(dates);
    },
    [mode, handleModifiedSave]
  );

  const handleBeforeCloseMenu = useCallback(() => {
    let result = true;

    if ((modifiedDates && !newDates) || (!modifiedDates && newDates)) {
      result = false;
    }

    const selectedDaysFrom = modifiedDates?.from;
    const selectedDaysTo = modifiedDates?.to;
    const newDatesFrom = newDates?.from;
    const newDatesTo = newDates?.to;

    if (
      selectedDaysFrom &&
      newDatesFrom &&
      selectedDaysTo &&
      newDatesTo &&
      (moment(selectedDaysFrom).format("DD-MM-YYYY") !==
        moment(newDatesFrom).format("DD-MM-YYYY") ||
        moment(selectedDaysTo).format("DD-MM-YYYY") !==
          moment(newDatesTo).format("DD-MM-YYYY"))
    ) {
      result = false;
    }

    if (!result) {
      setShowDiscardModal(true);
    }

    return result;
  }, [modifiedDates, newDates]);

  const handleClose = useCallback(() => {
    if (mode !== "atomic" || handleBeforeCloseMenu()) {
      typeof onCancel === "function" && onCancel();
      setShowDatePicker(false);
    }
  }, [mode, onCancel, handleBeforeCloseMenu]);

  const handleCancel = useCallback(
    (event) => {
      event && event.preventDefault();
      return handleClose();
    },
    [handleClose]
  );

  const handleShowDatePicker = useCallback(() => {
    setShowDatePicker(!showDatePicker);
  }, [showDatePicker]);

  const handleCloseDiscardModal = useCallback(() => {
    setShowDiscardModal(false);
  }, []);

  const handleClear = useCallback(() => {
    if (!dateRequired) {
      setNewDates(undefined);
      if (mode === "instant") {
        handleModifiedSave(undefined);
      }
    }
  }, [mode, dateRequired, handleModifiedSave]);

  const handleSaveDiscardModal = useCallback(() => {
    setNewDates(modifiedDates);
    setShowDiscardModal(false);
    setShowDatePicker(false);
  }, [modifiedDates]);

  const modifiers = useMemo(() => {
    const userAvailabilities = daysToModify.filter(
      (data) => data.type === "user_day_off"
    );
    const globalDaysOff = daysToModify.filter(
      (data) => data.type === "global_day_off"
    );
    const selectableGlobalDaysOff = daysToModify.filter(
      (data) => data.type === "selectable_global_day_off"
    );
    return {
      userAvailability: (day: Date): boolean => {
        // construct new date because react-day-picker returns date with hours set
        const currentDay = new Date(
          day.getFullYear(),
          day.getMonth(),
          day.getDate()
        );
        return userAvailabilities.some((data) =>
          isDayInRange(currentDay, data)
        );
      },
      weekend: (day: Date): boolean => {
        return weekends.some((value) => day.getDay() === value);
      },
      nonWorkingDay: (day: Date): boolean => {
        // return true if day is weekend day
        if (weekends.some((value) => value === day.getDay())) {
          return true;
        }
        const nonWorkingDays = [...selectableGlobalDaysOff, ...globalDaysOff];
        const currentDay = new Date(
          day.getFullYear(),
          day.getMonth(),
          day.getDate()
        );
        return nonWorkingDays.some((data) => isDayInRange(currentDay, data));
      },
      day_disabled: (day: Date): boolean => {
        if (disableDaysBefore) {
          if (
            day.toJSON().slice(0, 10) < disableDaysBefore.toJSON().slice(0, 10)
          ) {
            return true;
          }
        }
        // return true if day is weekend day
        if (
          !weekendIsSelectable &&
          weekends.some((value) => value === day.getDay())
        ) {
          return true;
        }
        // construct new date because react-day-picker returns date with hours set
        const currentDay = new Date(
          day.getFullYear(),
          day.getMonth(),
          day.getDate()
        );
        return globalDaysOff.some((data) => isDayInRange(currentDay, data));
      },
      nonWorkingDaysOfWeek: {
        dayOfWeek: nonWorkingDaysOfWeek,
      },
    };
  }, [
    daysToModify,
    nonWorkingDaysOfWeek,
    weekends,
    disableDaysBefore,
    weekendIsSelectable,
  ]);

  const renderDay = useCallback(
    (props: DayProps): JSX.Element | null => {
      const titles: string[] = [];
      if (weekends.some((value) => props.date.getDay() === value)) {
        titles.push(weekendLabel);
      }

      if (nonWorkingDaysOfWeek.includes(props.date.getDay())) {
        titles.push(nonWorkingDaysOfWeekLabel);
      }

      // construct new date because react-day-picker returns date with hours set
      const currentDay = new Date(
        props.date.getFullYear(),
        props.date.getMonth(),
        props.date.getDate()
      );

      daysToModify.forEach((data) => {
        if (isDayInRange(currentDay, data)) {
          titles.push(data.title);
        }
      });
      const child = (
        <div className="c-DayPicker-Day-Number">{props.date.getDate()}</div>
      );

      if (titles.length) {
        const title = (
          <div key={`title-wrapper-${props.date.getDate()}`}>
            {titles.map((title: string, index: number) => (
              <div key={`title-text-${index}`}>{title}</div>
            ))}
          </div>
        );

        return (
          <Tooltip
            title={title}
            popperTooltipClassName={popperTooltipClassName}
            popperTooltipStyle={popperTooltipStyle}
          >
            {child}
          </Tooltip>
        );
      }

      return child;
    },
    [
      weekends,
      nonWorkingDaysOfWeek,
      daysToModify,
      weekendLabel,
      nonWorkingDaysOfWeekLabel,
      popperTooltipClassName,
      popperTooltipStyle,
    ]
  );

  const renderDatePickerForm = useCallback((): JSX.Element => {
    return (
      <DatePickerForm
        onMonthChange={handleMonthChange}
        month={month}
        mode={mode}
        selectionMode={selectionMode}
        dateRequired={dateRequired}
        selectedDays={newDates}
        firstDayOfWeek={firstDayOfWeek}
        saveButtonText={saveButtonText}
        cancelButtonText={cancelButtonText}
        clearButtonText={clearButtonText}
        onDayClick={onDayClick}
        onSave={handleSave}
        onChange={handleChange}
        onCancel={handleCancel}
        onClear={handleClear}
        disabledSaveButton={disabledSaveButton}
        modifiers={modifiers}
        renderDay={renderDay}
      />
    );
  }, [
    handleMonthChange,
    month,
    mode,
    selectionMode,
    dateRequired,
    newDates,
    firstDayOfWeek,
    saveButtonText,
    cancelButtonText,
    clearButtonText,
    onDayClick,
    handleSave,
    handleChange,
    handleCancel,
    handleClear,
    disabledSaveButton,
    modifiers,
    renderDay,
  ]);

  const renderTargetEl = useMemo(() => {
    if (labelType === "icon" && icon) {
      return (
        <SelectDateTarget
          icon={icon}
          title={tooltipText}
          targetIconClassName={targetIconClassName}
          forceHideTooltip={showDatePicker}
          popperTooltipClassName={popperTooltipClassName}
          popperTooltipStyle={popperTooltipStyle}
          active={showDatePicker}
        />
      );
    }
    if (typeof labelType === "function") {
      return labelType(labelText);
    }
    return (
      <StyledSelectDateButton
        type="button"
        className={classnames("date-picker-target", targetClassName)}
      >
        <span className={targetTextClassName}>{labelText}</span>
      </StyledSelectDateButton>
    );
  }, [
    icon,
    labelText,
    labelType,
    popperTooltipClassName,
    popperTooltipStyle,
    showDatePicker,
    targetClassName,
    targetIconClassName,
    targetTextClassName,
    tooltipText,
  ]);

  return (
    <StyledSelectDate className="select-date">
      {labelType !== "inline" ? (
        <Menu
          target={renderTargetEl}
          open={showDatePicker}
          onOpen={handleShowDatePicker}
          onBeforeClose={handleBeforeCloseMenu}
          onClose={handleClose}
          position={position}
          menuClassName={menuClassName}
          popperClassName={popperClassName}
          backgroundElementClass={backgroundElementClass}
        >
          {renderDatePickerForm()}
        </Menu>
      ) : (
        renderDatePickerForm()
      )}
      {mode === "atomic" ? (
        <StyledConfirmDialog
          className="modal-select-date"
          open={showDiscardModal}
          onCancel={handleCloseDiscardModal}
          onConfirm={handleSaveDiscardModal}
          dialogTitle={modalHeaderText}
          dialogContent={modalDiscardMessage}
          cancelBtnText={modalCancelBtnText}
          confirmBtnText={modalDiscardBtnText}
        />
      ) : null}
    </StyledSelectDate>
  );
};

SelectDate.displayName = "SelectDate";
